import { Download } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@aeris/ui'

import { saveFile } from '../lib/export/save-file.ts'
import {
  collectPages,
  exportFileName,
  MAX_EXPORT_ROWS,
  toCsv,
  toGrid,
  toJson,
  type ExportColumn,
  type ExportFormat,
} from '../lib/export/table-export.ts'
import { xlsxBytes } from '../lib/export/xlsx-writer.ts'

/**
 * "Exportar": as colunas da tela, todas as páginas do filtro — F131.
 *
 * O botão mora ao lado de "Colunas" porque é a continuação dele: o que foi
 * escolhido ali é o que sai no arquivo. Enquanto baixa as páginas, o próprio
 * botão conta o andamento; uma lista de 5 mil linhas leva alguns segundos, e
 * um botão parado nesse tempo é clicado de novo.
 */

const FORMATS: readonly {
  format: ExportFormat
  label: string
  hint: string
}[] = [
  { format: 'xlsx', label: 'Planilha (XLSX)', hint: 'Excel, Google Planilhas' },
  { format: 'csv', label: 'CSV', hint: 'Excel em português, ponto e vírgula' },
  { format: 'json', label: 'JSON', hint: 'Para outro sistema ler' },
]

const MIME: Record<ExportFormat, string> = {
  csv: 'text/csv;charset=utf-8',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  json: 'application/json;charset=utf-8',
}

export type ExportMenuProps<Row> = {
  /** `produtos` → `produtos-2026-09-13.xlsx`. */
  readonly fileName: string
  readonly sheetName: string
  readonly columns: readonly ExportColumn<Row>[]
  /** Uma página do filtro que está na tela, com o tamanho máximo do servidor. */
  readonly fetchPage: (
    page: number
  ) => Promise<{ readonly items: readonly Row[]; readonly total: number }>
  readonly pageSize: number
  /** Quantos o filtro tem agora; zero desabilita. */
  readonly total: number
}

export const ExportMenu = <Row,>({
  fileName,
  sheetName,
  columns,
  fetchPage,
  pageSize,
  total,
}: ExportMenuProps<Row>): ReactNode => {
  const [progress, setProgress] = useState<{
    done: number
    total: number
  } | null>(null)
  const [message, setMessage] = useState<{
    tone: 'error' | 'info'
    text: string
  } | null>(null)

  const run = async (format: ExportFormat): Promise<void> => {
    setMessage(null)
    setProgress({ done: 0, total })
    try {
      const collected = await collectPages(fetchPage, {
        pageSize,
        onProgress: (done, of) => {
          setProgress({ done, total: of })
        },
      })

      const name = exportFileName(fileName, format)
      if (format === 'xlsx') {
        saveFile(
          name,
          MIME.xlsx,
          xlsxBytes(
            sheetName,
            columns.map((column) => column.label),
            toGrid(columns, collected.rows)
          )
        )
      } else {
        saveFile(
          name,
          MIME[format],
          format === 'csv'
            ? toCsv(columns, collected.rows)
            : toJson(columns, collected.rows)
        )
      }

      if (collected.truncated) {
        setMessage({
          tone: 'info',
          text: `O arquivo tem as primeiras ${MAX_EXPORT_ROWS.toLocaleString('pt-BR')} linhas de ${collected.total.toLocaleString('pt-BR')}. Filtre a lista para exportar o resto.`,
        })
      }
    } catch {
      setMessage({
        tone: 'error',
        text: 'Não foi possível exportar. Verifique a conexão e tente de novo.',
      })
    } finally {
      setProgress(null)
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            disabled={total === 0 || progress !== null}
            loading={progress !== null}
            leadingIcon={<Download aria-hidden="true" className="h-4 w-4" />}
          >
            {progress === null
              ? 'Exportar'
              : `Exportando ${String(progress.done)} de ${String(progress.total)}`}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>
            {`${String(columns.length)} ${columns.length === 1 ? 'coluna' : 'colunas'} da tela · ${total.toLocaleString('pt-BR')} ${total === 1 ? 'linha' : 'linhas'}`}
          </DropdownMenuLabel>
          {FORMATS.map((entry) => (
            <DropdownMenuItem
              key={entry.format}
              onSelect={() => {
                void run(entry.format)
              }}
            >
              <span className="flex flex-col">
                <span>{entry.label}</span>
                <span className="text-(length:--text-xs) text-(--text-muted)">
                  {entry.hint}
                </span>
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {message === null ? null : (
        <p
          role={message.tone === 'error' ? 'alert' : 'status'}
          className={`max-w-72 text-right text-(length:--text-xs) ${message.tone === 'error' ? 'text-(--danger)' : 'text-(--text-secondary)'}`}
        >
          {message.text}
        </p>
      )}
    </div>
  )
}
